import { useCallback, useEffect, useState } from 'react';
import amplitude from 'src/_shared/amplitude';
import { AnalyticsEvents } from 'src/_shared/analyticsEvents';
import { logError } from 'src/_shared/logger';
import { SECTION_NAMES } from 'src/constants/sectionNames.contants';
import { useStore } from 'src/contexts/StoreContext';
import { useProductLogic } from 'src/hooks/use-product/useProduct.logic';
import { useDebounceCallback } from 'src/hooks/useDebounceCallback';
import { ProductModelInstance } from 'src/store/_models/product';

/**
 * useQuantityItemLogic
 *
 * Handles hover state for the action icons in QuantityItem.
 * The actual fade animations are handled by FadeIn/FadeOut in the component.
 */

export const useQuantityItemLogic = (product: ProductModelInstance) => {
  const [productCount, setProductCount] = useState(1);
  const { removeProduct: removeProductFromStore } = useProductLogic();

  const {
    project: { activeProject },
  } = useStore();

  const [isTextInputFocused, setIsTextInputFocused] = useState(false);

  useEffect(() => {
    setProductCount(product.quantity);
  }, [product]);

  const updateProductQuantitySDKAPIDebounced = useDebounceCallback(
    async (product: ProductModelInstance, newQuantity: number) => {
      try {
        const finalQuantity = await activeProject?.updateProductQuantity(product, newQuantity);
        setProductCount(finalQuantity);
      } catch (error) {
        logError(error);
      }

      return newQuantity;
    },
    500,
  );

  const addProduct = useCallback(() => {
    setProductCount(count => {
      const newCount = count + 1;
      amplitude.trackEvent(AnalyticsEvents.personalize.modifyNCopyClick, {
        action: 'plus',
        current_n_copy: newCount,
      });
      updateProductQuantitySDKAPIDebounced(product, newCount);
      return newCount;
    });
  }, [product]);

  const subtractProduct = useCallback(() => {
    setProductCount(count => {
      const newCount = count - 1;
      amplitude.trackEvent(AnalyticsEvents.personalize.modifyNCopyClick, {
        action: 'minus',
        current_n_copy: newCount,
      });
      updateProductQuantitySDKAPIDebounced(product, newCount);
      return newCount;
    });
  }, [product]);

  const removeProduct = useCallback(
    ({ sectionName }: { sectionName?: SECTION_NAMES }) => removeProductFromStore({ product, sectionName }),
    [product],
  );

  const updateProduct = useCallback(() => {
    const newQuantity = typeof productCount === 'number' && productCount > 0 ? productCount : 1;
    updateProductQuantitySDKAPIDebounced(product, newQuantity);
  }, [product, productCount]);

  const onChangeProductCountLocale = useCallback((pCount: string) => {
    // remove other chars except numbers
    let newPCount = pCount.replace(/\D/g, '');
    if (newPCount.length > 4) newPCount = '9999';
    setProductCount(parseInt(newPCount));
  }, []);

  return {
    addProduct,
    subtractProduct,
    removeProduct,
    updateProduct,
    productCount,
    onChangeProductCountLocale,
    isTextInputFocused,
    setIsTextInputFocused,
  };
};
